import { useState, useEffect } from "react";
import { Progress, Upload, message } from "antd";
import type { UploadProps } from "antd";
import { useNewGuideFormStore } from "state/zustand-stores/new-guide-form-store";
import { useVideoMetadata } from "hooks/useVideoMetadata";

import { IoIosArrowBack } from "react-icons/io";

interface NewGuideAddVideoProps {
	onUpload?: (file: File) => void;
	onRecord?: () => void;
	onNext?: () => void;
	isUploading: boolean;
	uploadProgress?: number;
}

const TipsList = () => (
	<div className="new-guide-add-video-tips-box">
		<div className="new-guide-add-video-tips-header">
			<img src={`${process.env.PUBLIC_URL}/assets/images/icons/info-circle.svg`} />
			<span>Helpful tips for a great video:</span>
		</div>
		<ol className="new-guide-add-video-tips-list">
			<li>
				<b>Show, don’t just tell</b> - Point the camera at what you’re doing rather than just talking through steps.
			</li>
			<li>
				<b>Keep it focused</b> - Aim for 5-10 minutes max.
			</li>
			<li>
				<b>Audio matters</b> - Speak clearly so that your instructions are easy to follow.
			</li>
			<li>
				<b>No need for perfection</b> - Our AI will handle all of the formatting and polish. You can review and edit
				your Guide before publishing.
			</li>
		</ol>
	</div>
);

const NewGuideAddVideo = ({
	onRecord = () => {},
	onNext = () => {},
	isUploading,
	uploadProgress = 0
}: NewGuideAddVideoProps) => {
	// Use Zustand store for video state management
	const { formData, setVideo, previousStep } = useNewGuideFormStore();
	const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
	const videoMetadata = useVideoMetadata(formData.video);
	// Get selected video from store
	const selectedVideo = formData.video;

	// Create preview URL when video changes
	useEffect(() => {
		if (selectedVideo && selectedVideo instanceof File) {
			const previewUrl = URL.createObjectURL(selectedVideo);
			setVideoPreviewUrl(previewUrl);

			// Cleanup function to revoke URL when component unmounts or video changes
			return () => {
				URL.revokeObjectURL(previewUrl);
			};
		} else {
			setVideoPreviewUrl(null);
		}
	}, [selectedVideo]);

	// Check video duration when metadata is available
	useEffect(() => {
		if (selectedVideo && videoMetadata && "duration" in videoMetadata && typeof videoMetadata.duration === "number") {
			const maxDurationInSeconds = 45 * 60; // 45 minutes

			if (videoMetadata.duration > maxDurationInSeconds) {
				message.error("This video exceeds the duration limit of 45 minutes. Please try again with a shorter video.");
				setVideo(null);
			} else {
				message.success(`${selectedVideo.name} file added successfully`);
			}
		}
	}, [videoMetadata, selectedVideo, setVideo]);

	const handleVideoRemove = () => {
		setVideo(null);
		message.info("Video removed");
	};

	const uploadProps: UploadProps = {
		accept: "video/*",
		multiple: false,
		maxCount: 1,
		beforeUpload: file => {
			const isVideo = file.type.startsWith("video/");
			if (!isVideo) {
				message.error("You can only upload video files!");
				return false;
			}

			const isLt2GB = file.size <= 2 * 1024 * 1024 * 1024; // allow files up to 2GB
			if (!isLt2GB) {
				message.error("Video must be smaller than 2GB!");
				return false;
			}

			setVideo(file);
			return false; // Prevent default upload behavior
		},
		onRemove: handleVideoRemove,
		showUploadList: false // We'll handle the preview ourselves
	};

	return (
		<div className="new-guide-add-video-video-card">
			<div className="new-guide-add-video-back-button" onClick={previousStep}>
				<IoIosArrowBack size={19} color={"#404149"} style={{ marginRight: "4px" }} />
				Back
			</div>
			<div className="new-guide-description-card-title">Add a how-to video.</div>
			{/* Video Preview Section */}
			{selectedVideo && videoPreviewUrl ? (
				<>
					<div className="new-guide-add-video-video-preview-section">
						<div className="new-guide-add-video-preview-container">
							<video src={videoPreviewUrl} className="new-guide-add-video-video-preview" controls={false} muted />
							<div className="new-guide-add-video-video-filename">{selectedVideo.name}</div>
							{!isUploading && (
								<button
									type="button"
									className="new-guide-add-video-remove-video-button"
									onClick={handleVideoRemove}
									aria-label="Remove video"
								>
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`} alt="Remove" />
								</button>
							)}
						</div>
					</div>

					<div className="new-guide-add-video-add-video-actions-row">
						<button
							type="button"
							className={`new-guide-add-video-create-guide-button highnote-button`}
							onClick={onNext}
							disabled={isUploading}
						>
							{isUploading ? (
								<Progress
									type="circle"
									percent={Math.round(uploadProgress)}
									strokeColor="#1890ff"
									size={30}
									strokeWidth={6}
									showInfo={false}
									className="new-guide-add-video-file-attachment-input-progress"
								/>
							) : (
								<>
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/twinkle-star.svg`} />
									Create Guide
								</>
							)}
						</button>
					</div>
				</>
			) : (
				<>
					<div>
						<div className="new-guide-description-card-section-title">Upload a video below.</div>
						<TipsList />
					</div>

					<div className="new-guide-add-video-add-video-actions-row">
						<Upload {...uploadProps} className="new-guide-add-video-upload-component">
							<button type="button" className="highnote-button">
								Select a Video
							</button>
						</Upload>
						{/* <button className="new-guide-add-video-record-button" onClick={onRecord}>
              Start Recording
            </button> */}
					</div>
				</>
			)}
		</div>
	);
};

export default NewGuideAddVideo;
